'use client';

import classNames from '@sindresorhus/class-names';
import React, {
  PropsWithChildren,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';

import {Icon} from '@web-catalog/ui-kit/Icon';
import {IconButton} from '@web-catalog/ui-kit/IconButton';
import {
  ChevronLeftOutline32Svg,
  ChevronRightOutline32Svg,
} from '@web-catalog/ui-kit/SvgIcons';

import styles from './Scroller.module.scss';

const debounce = (fn: (immediate: boolean) => void, delayMs: number) => {
  let timeout: NodeJS.Timeout | null = null;
  return () => {
    if (!timeout) {
      fn(true);
    }
    timeout && clearTimeout(timeout);

    timeout = setTimeout(() => {
      timeout = null;
      fn(false);
    }, delayMs);
  };
};

type Props = {
  /** имена классов для кастомизации */
  classes?: Partial<{
    /** клас для основного контейнера */
    root: string;
    /** клас для скроллящегося контейнера */
    scrollable: string;
    /** класс для кнопки назад */
    prevNavButton: string;
    /** класс для кнопки вперед */
    nextNavButton: string;
  }>;
  /**
   * по умолчанию компонент ставит на скроллящийся элемент класс со scroll-snap-type: x mandatory
   * (и еще мальца магии на детей, чтобы они позиционировались слева или по центру в зависимости от брейкпойнта)
   * если передан disableDefaultScrollableContainerClass=false то это все не ставится
   */
  disableDefaultScrollableContainerClass?: boolean;
  /**
   * если передано число, то кнопки назад-вперед будут скроллить на эту фиксированную величину,
   * если auto, то по кнопкам будет скроллить так, чтобы показался элемент,
   *  предшествующий или следующий за текущими видимыми элементами
   */
  navButtonsScrollBy?: number | 'auto';
  /** на анмаунте передаст сюда последний известный scrollLeft (юзать в паре с getStoredScrollOffset) */
  storeScrollOffset?: (offset: number) => void;
  /** на маунте может восстановить scrollLeft по значению из этого колбека (юзать в паре с storeScrollOffset) */
  getStoredScrollOffset?: () => number;
  onSwipe?: (direction: 'left' | 'right') => void;
  testId?: string;
};

export const Scroller = ({
  testId,
  disableDefaultScrollableContainerClass = false,
  classes,
  navButtonsScrollBy = 'auto',
  getStoredScrollOffset,
  storeScrollOffset,
  onSwipe,
  children,
}: PropsWithChildren<Props>) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [canGoBack, setCanGoBack] = useState(false);
  const [canGoForward, setCanGoForward] = useState(false);
  const lastScrollLeftRef = useRef(0);
  const elementsVisibilityRef = useRef<Array<boolean>>([]);
  const onSwipeRef = useRef(onSwipe);

  useEffect(() => {
    onSwipeRef.current = onSwipe;
  }, [onSwipe]);

  const onNextOrPrev = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      const {direction} = e.currentTarget.dataset;
      if (navButtonsScrollBy !== 'auto' || !window.IntersectionObserver) {
        containerRef.current?.scrollBy({
          left:
            (typeof navButtonsScrollBy === 'number'
              ? navButtonsScrollBy
              : 300) * (direction === 'prev' ? -1 : 1),
          behavior: 'smooth',
        });
        return;
      }

      const container = containerRef.current;
      if (!container) {
        return;
      }
      const firstVisibleItem = elementsVisibilityRef.current.findIndex(Boolean);
      const newFirstVisibleItem =
        direction === 'next' ? firstVisibleItem + 1 : firstVisibleItem - 1;
      const child = container.children[newFirstVisibleItem] as HTMLElement;
      if (child) {
        container.scrollTo({left: child.offsetLeft, behavior: 'smooth'});
      }
    },
    [navButtonsScrollBy],
  );

  useLayoutEffect(() => {
    const scrollableContainer = containerRef.current;
    if (!scrollableContainer) {
      return;
    }

    const cleanupCallbacks: Array<VoidFunction> = [];

    if (window.IntersectionObserver && navButtonsScrollBy === 'auto') {
      const childrenArray = [...scrollableContainer.children];

      const io = new IntersectionObserver(
        (entries) => {
          entries.forEach(({target, isIntersecting}) => {
            const index = childrenArray.indexOf(target);
            elementsVisibilityRef.current[index] = isIntersecting;
          });
        },
        {threshold: 0.99, rootMargin: '1000px 0px 1000px 0px'},
      );
      childrenArray.forEach((item) => io.observe(item));
      cleanupCallbacks.push(() => io.disconnect());
    }

    setCanGoForward(
      scrollableContainer.scrollWidth > scrollableContainer.clientWidth,
    );

    if (getStoredScrollOffset) {
      const initialScrollOffset = getStoredScrollOffset();
      if (initialScrollOffset) {
        scrollableContainer.scrollTo({
          left: initialScrollOffset,
          behavior: 'instant',
        });
      }
    }

    let scrollStartPosition = 0;
    const scrollListener = debounce((immediate) => {
      const {scrollLeft, offsetWidth, scrollWidth} = scrollableContainer;
      if (immediate) {
        scrollStartPosition = scrollLeft;
      } else {
        const delta = scrollLeft - scrollStartPosition;
        onSwipeRef.current?.(delta > 0 ? 'left' : 'right');
      }
      lastScrollLeftRef.current = scrollLeft;
      setCanGoBack(scrollLeft > 0);
      setCanGoForward(
        Math.abs(Math.floor(scrollLeft + offsetWidth) - scrollWidth) > 5,
      );
    }, 50);
    scrollableContainer.addEventListener('scroll', scrollListener);

    cleanupCallbacks.push(() =>
      scrollableContainer.removeEventListener('scroll', scrollListener),
    );

    return () => {
      storeScrollOffset?.(lastScrollLeftRef.current);
      cleanupCallbacks.forEach((cb) => cb());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div
      className={classNames(styles.root, classes?.root)}
      data-testid={testId}
    >
      <div
        ref={containerRef}
        className={classNames(
          styles.scrollableContainer,
          classes?.scrollable,
          !disableDefaultScrollableContainerClass &&
            styles.scrollableContainerWithSnap,
        )}
      >
        {children}
      </div>
      {canGoForward && (
        <IconButton
          className={classNames(
            styles.control,
            styles.controlNext,
            classes?.nextNavButton,
          )}
          aria-label="Скролл вперед"
          onClick={onNextOrPrev}
          data-direction="next"
        >
          <Icon svg={ChevronRightOutline32Svg} />
        </IconButton>
      )}
      {canGoBack && (
        <IconButton
          className={classNames(
            styles.control,
            styles.controlPrev,
            classes?.prevNavButton,
          )}
          aria-label="Скролл назад"
          onClick={onNextOrPrev}
          data-direction="prev"
        >
          <Icon svg={ChevronLeftOutline32Svg} />
        </IconButton>
      )}
    </div>
  );
};
